import * as yup from 'yup';
import { Check, Copy, UserMinus, Users } from 'react-feather';
import { DocumentNode } from 'graphql';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import {
  GetTeamDetailsQuery as GetTeamDetails,
  JoinPolicy,
  RegistrationStatus,
} from '__generated__/graphql';

import { DropdownSection, useDropdownSectionState } from '../../Common/Layout';
import { RegistrationSummary, Team } from '../helpers';
import { getServerErrors } from '../../../common/helpers';
import { useCopyLink } from '../../../common/Common/CopyLink';
import { useGuard } from '../Guard';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import EditTeamMutation from './EditTeamMutation';
import GetRegistrationDetailsQuery from './GetRegistrationDetailsQuery';
import GetRegistrationSummaryForDashboardQuery from './GetRegistrationSummaryForDashboardQuery';
import GetTeamDetailsQuery from './GetTeamDetailsQuery';
import RemoveTeamMemberMutation from './RemoveTeamMemberMutation';
import UI from '../../../common/UI';
import useConfirmModal from '../../../common/useConfirmModal';
import useMutation from '../../../api/useMutation';
import useQuery from '../../../api/useQuery';
import useValidation from '../../../common/useValidation';

type Member = GetTeamDetails['team']['members'][0];

const canBeRemoved = (member: Member) => member.ticket.allow_individuals && !member.locked;

export interface TeamDetailsProps {
  team: Team;
  summary: RegistrationSummary;
  open?: boolean;
  onClose?: () => void;
  refetchQueries?: DocumentNode[];
}

const TeamDetails = ({
  team, summary, open, onClose, refetchQueries,
}: TeamDetailsProps) => {
  const { guard } = useGuard(summary.participant);

  const { isOpen, toggleSection, openSection, closeSection } = useDropdownSectionState({
    initialState: open,
    onClose,
  });

  const openRef = useRef(open);

  useEffect(() => {
    if (open !== openRef.current) {
      if (open) {
        openSection();
      } else {
        closeSection();
      }

      openRef.current = open;
    }
  }, [open, openSection, closeSection]);

  return (
    <DropdownSection
      open={isOpen()}
      onToggle={() => guard(() => toggleSection())}
      title={team.title}
      preview={(
        <UI.FlexContainer sc={{ justifyContent: 'space-between' }}>
          <UI.H4>
            <UI.Icon>
              <Users />
            </UI.Icon>
            {' '}
            {team.title}
          </UI.H4>
          {team.team_type.title && (
            <UI.Label sc={{ small: true }}>
              {team.team_type.title}
            </UI.Label>
          )}
        </UI.FlexContainer>
      )}
      body={(
        <TeamBody teamId={team.id} summary={summary} refetchQueries={refetchQueries} />
      )}
    />
  );
};

interface TeamBodyProps {
  teamId: string;
  summary: RegistrationSummary;
  refetchQueries?: DocumentNode[];
}

const TeamBody = ({ teamId, summary, refetchQueries }: TeamBodyProps) => {
  const { t } = useTranslation();

  const { data } = useQuery(
    GetTeamDetailsQuery,
    {
      variables: {
        id: teamId,
      },
    },
  );

  const team = data?.team;
  const isCaptain = team && summary.participant.id === team.captain.id;

  const hasRemovableMembers = isCaptain && team && team.members
    .reduce((anyMemberRemovable, member) => anyMemberRemovable || canBeRemoved(member), false);

  const inputRef = useRef<HTMLInputElement>();
  const buttonRef = useRef<HTMLButtonElement>();
  const [copy, copied] = useCopyLink(team?.access_code);

  if (!team) {
    return (
      <UI.Div sc={{ padding: [3, 4] }}>
        <UI.PageLoader />
      </UI.Div>
    );
  }

  return (
    <UI.FormGrid sc={{ padding: [3, 4] }}>
      {isCaptain && team.team_type.join_policy === JoinPolicy.private && (
        <UI.InputGroup>
          <UI.InputLabel htmlFor={`TeamAccessCode_${team.id}`}>
            {t('common:team_picker.access_code')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('team_share_access_code_description')}
          </UI.InputDescription>
          <UI.InputWrapper>
            <UI.Input
              defaultValue={team.access_code}
              readOnly
              onFocus={(event) => event.target.select()}
              ref={inputRef}
              id={`TeamAccessCode_${team.id}`}
            />
            <UI.Button
              onClick={() => copy()}
              sc={{ blank: true, noWrap: true }}
              ref={buttonRef}
              style={{ borderLeft: '1px solid rgba(0, 0, 0, .075)' }}
            >
              <UI.Icon>
                {copied ? <Check /> : <Copy />}
              </UI.Icon>
              {' '}
              {t('copy')}
            </UI.Button>
          </UI.InputWrapper>
        </UI.InputGroup>
      )}

      <UI.Div>
        <UI.InputLabel>
          {t('team_members')}
        </UI.InputLabel>

        <UI.OL sc={{ mb: 0 }}>
          {team.members.map((member) => (
            <UI.LI sc={{ color: member.full_name ? undefined : 'gray.400' }} key={member.id}>
              <TeamMember
                key={member.id}
                member={member}
                hasRemovableMembers={hasRemovableMembers}
              />
            </UI.LI>
          ))}
        </UI.OL>

        {team.members.length === 0 && (
          <UI.Div sc={{ muted: true }}>
            {t('no_team_members_yet')}
          </UI.Div>
        )}
      </UI.Div>

      {isCaptain && (
        <>
          <UI.HR />

          <EditTeamForm team={team} refetchQueries={refetchQueries} />
        </>
      )}
    </UI.FormGrid>
  );
};

interface TeamMemberProps {
  member: Member;
  hasRemovableMembers: boolean;
  refetchQueries?: DocumentNode[];
}

const TeamMember = ({
  member,
  hasRemovableMembers,
  refetchQueries = [GetTeamDetailsQuery],
}: TeamMemberProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const [removeMember, { loading: removing }] = useMutation(
    RemoveTeamMemberMutation,
    {
      variables: {
        id: member.id,
      },
      onCompleted: () => {
        notifier.success(t('team_member_removed'));
      },
      refetchQueries,
      awaitRefetchQueries: refetchQueries.length > 0,
    },
  );

  const [confirmRemove] = useConfirmModal({
    title: member.full_name
      ? t('remove_team_member_name', { name: member.full_name })
      : t('remove_team_member'),
    body: t('remove_team_member_description'),
    onConfirm: () => removeMember(),
  }, [member]);

  return (
    <UI.GridContainer
      sc={{
        columns: hasRemovableMembers ? '1fr 128px 28px' : '1fr 128px',
        alignVertical: 'center',
        gutter: 0.5,
        py: 0.5,
      }}
      key={member.id}
    >
      <UI.Div sc={{ fontWeight: 500 }}>
        {member.full_name || t('not_assigned_yet')}
        {member.status === RegistrationStatus.preregistration && (
          <>
            {' '}
            <UI.Label sc={{ small: true, mt: 0.5, mr: 1 }}>
              <UI.Icon>
                <UI.Icons.Pending />
              </UI.Icon>
              {' '}
              {t('pending')}
            </UI.Label>
          </>
        )}
      </UI.Div>
      <UI.Div sc={{ small: true, textAlign: 'right' }}>
        <UI.Truncate>
          <UI.Icon>
            <UI.Icons.Ticket />
          </UI.Icon>
          {' '}
          {member.ticket.title}
        </UI.Truncate>
      </UI.Div>
      {hasRemovableMembers && (
        <UI.Div sc={{ textAlign: 'right' }}>
          <UI.Button
            onClick={() => confirmRemove()}
            sc={{ size: 'sm', outline: true }}
            disabled={removing || !canBeRemoved(member)}
            aria-label={
              member.full_name
                ? t('remove_team_member_name', { name: member.full_name })
                : t('remove_team_member')
            }
          >
            <UI.Icon>
              <UserMinus />
            </UI.Icon>
          </UI.Button>
        </UI.Div>
      )}
    </UI.GridContainer>
  );
};

interface EditTeamFormProps {
  team: Team;
  refetchQueries?: DocumentNode[];
}

const EditTeamForm = ({
  team,
  refetchQueries = [GetRegistrationSummaryForDashboardQuery, GetRegistrationDetailsQuery],
}: EditTeamFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      title: team.title,
    },
  });

  const [editTeam, { loading: editing, error }] = useMutation(
    EditTeamMutation,
    {
      variables: {
        input: {
          id: team.id,
          title: form.values.title,
        },
      },
      onCompleted: ({ editTeam: team }) => {
        notifier.success(t('information_saved'));
        form.reset({ title: team.title });
      },
      refetchQueries,
      awaitRefetchQueries: refetchQueries.length > 0,
    },
  );

  const { errors, valid } = useValidation({
    schema: teamSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editTeam()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />
        <UI.InputGroup sc={{ mb: 0, valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel sc={{ mb: 1 }} htmlFor="TeamTitle">
            {t('team_name')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.Div>
            <UI.FormGrid sc={{ columns: '1fr fit-content(150px)', gutter: 0.5 }}>
              <UI.Div>
                <UI.DebouncedInput {...form.text('title')} id="TeamTitle" />
              </UI.Div>
              <UI.Div>
                <UI.Button
                  onClick={() => editTeam()}
                  sc={{ brand: 'secondary', loading: editing }}
                  disabled={editing || !valid || !form.changed()}
                >
                  {t('save')}
                </UI.Button>
              </UI.Div>
            </UI.FormGrid>
            <UI.ErrorMessages attribute={t('team_name')} errors={errors.title} />
          </UI.Div>
        </UI.InputGroup>
      </UI.FormGrid>
    </UI.Form>
  );
};

const teamSchema = yup.object().shape({
  title: yup.string().ensure().required(),
});

export default TeamDetails;
